/**
 * Social sharing (Workstream B). Builds a short bilingual share message + a deep link into the
 * daily challenge, then shares via the Web Share API (mobile/PWA) with a clipboard fallback
 * (desktop). When the link is posted, social platforms render the OG card (assets/og/og-card.png),
 * so virality rides on the link itself — no client-side image generation needed.
 */
import type { Language } from "../i18n";

const SITE = "https://duel-siege-ai.pages.dev";

/** Deep link that jumps a visitor straight into today's daily challenge. */
export function dailyDeepLink(): string {
  return `${SITE}/?daily=1`;
}

export function buildDailyShareText(lang: Language, opts: { score: number; streak: number; won: boolean }): string {
  const streakKo = opts.streak > 1 ? `, 연속 ${opts.streak}일` : "";
  const streakEn = opts.streak > 1 ? `, ${opts.streak}-day streak` : "";
  if (lang === "ko") {
    return `⚔️ Duel Siege AI 오늘의 도전 ${opts.won ? "클리어" : "도전"} — 점수 ${opts.score}${streakKo}! 날 이겨봐:`;
  }
  return `⚔️ I ${opts.won ? "cleared" : "took on"} today's Duel Siege AI daily gauntlet — score ${opts.score}${streakEn}! Beat me:`;
}

export function buildCareerShareText(lang: Language, opts: { rivalryStageLabel: string; nickname?: string; level: number }): string {
  if (lang === "ko") {
    return `⚔️ Duel Siege AI에서 AI 사령관과의 관계: ${opts.rivalryStageLabel}${opts.nickname ? ` · 별명 "${opts.nickname}"` : ""} · 지휘관 Lv ${opts.level}. 너도 도전해봐:`;
  }
  return `⚔️ My rivalry with the Duel Siege AI commander: ${opts.rivalryStageLabel}${opts.nickname ? ` · "${opts.nickname}"` : ""} · Commander Lv ${opts.level}. Take it on:`;
}

export type ShareOutcome = "shared" | "copied" | "failed";

type ShareCapableNavigator = Navigator & { share?: (data: ShareData) => Promise<void> };

/** Try the Web Share API first; fall back to copying "<text> <url>" to the clipboard. */
export async function shareOrCopy(payload: { title: string; text: string; url: string }): Promise<ShareOutcome> {
  const nav = navigator as ShareCapableNavigator;
  if (typeof nav.share === "function") {
    try {
      await nav.share({ title: payload.title, text: payload.text, url: payload.url });
      return "shared";
    } catch (error) {
      // User cancelled the share sheet → treat as done, don't also copy.
      if (error instanceof DOMException && error.name === "AbortError") return "shared";
      // Otherwise fall through to the clipboard fallback.
    }
  }
  try {
    await navigator.clipboard.writeText(`${payload.text} ${payload.url}`);
    return "copied";
  } catch {
    return "failed";
  }
}
